import React, { useMemo, useState } from "react";
import {
  Filter,
  ChevronDown,
  Tag,
  X,
  Check,
  User,
  Sparkles,
  Clock,
} from "lucide-react";
import { Input } from "components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { cn } from "lib/utils";

// Sentinel selected alongside real tool values to mean "tasks with no tool
// attached" (mirrors the box-flow-delight reference's "None" tool row).
export const NO_TOOL_VALUE = "__no_tool__";

const SHOW_OPTIONS = [
  { id: "you", label: "You", Icon: User },
  { id: "team", label: "Whole team", Icon: Sparkles },
  { id: "scheduled", label: "Scheduled", Icon: Clock },
  { id: "proven", label: "Proven", Icon: Sparkles },
];

const FILTER_TABS = [
  { id: "show", label: "Show" },
  { id: "team", label: "Team" },
  { id: "tools", label: "Tools" },
];

// A single filter row: a radio dot ("single" mode, the Show tab) or a
// checkbox ("multi" mode, Team/Tools), an optional icon, a label and a count.
const Row = ({ icon: Icon, label, count, active, mode = "multi", onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "w-full flex items-center gap-2.5 px-3 py-1.5 text-sm rounded-sm mx-1 text-left transition-colors",
      active ? "bg-muted text-foreground" : "hover:bg-muted/60",
    )}
  >
    {mode === "multi" ? (
      <span
        aria-hidden
        className={cn(
          "grid h-4 w-4 shrink-0 place-items-center rounded-sm border transition-colors",
          active
            ? "border-foreground bg-foreground text-background"
            : "border-border bg-background",
        )}
      >
        {active && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
    ) : (
      <span
        aria-hidden
        className={cn(
          "grid h-4 w-4 shrink-0 place-items-center rounded-full border transition-colors",
          active ? "border-foreground" : "border-border",
        )}
      >
        {active && <span className="h-2 w-2 rounded-full bg-foreground" />}
      </span>
    )}
    {Icon && (
      <span className="shrink-0 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
      </span>
    )}
    <span className={cn("flex-1 min-w-0 truncate", active && "font-medium")}>{label}</span>
    {typeof count === "number" && (
      <span className="text-[11px] text-muted-foreground tabular-nums">{count}</span>
    )}
  </button>
);

/**
 * The Task Library header for the chat landing, matching the box-flow-delight
 * Filter design: a "Task Library" eyebrow + live count on the left, and one
 * "Filter" pill on the right opening a popover with three tabs:
 *  - Show  — You / Whole team / Scheduled / Proven (single-select scope)
 *  - Team  — real teammates, multi-select, narrows "Whole team"
 *  - Tools — task search + the (real) tool list, grouped Connected/Available
 * The active scope (when not "You") surfaces as a removable chip, same as
 * selected tools.
 */
const TasksFilterBar = ({
  scope = "you",
  onScopeChange,
  scopeChipLabel,
  onResetScope,
  counts = {},
  count = 0,
  selectedTools = [],
  onToggleTool,
  toolOptions = [],
  noToolCount = 0,
  teamMembers = [],
  selectedTeamMembers = [],
  onToggleTeamMember,
  onClearAll,
  onSaveView,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTab, setFilterTab] = useState("show");
  const [ownerSearch, setOwnerSearch] = useState("");
  const [toolSearch, setToolSearch] = useState("");

  const filteredTeamMembers = useMemo(
    () =>
      teamMembers.filter(
        (m) => !ownerSearch || m.name.toLowerCase().includes(ownerSearch.toLowerCase()),
      ),
    [teamMembers, ownerSearch],
  );

  const filteredToolOptions = useMemo(
    () =>
      toolOptions.filter((opt) =>
        (opt.label || opt.value || "").toLowerCase().includes(toolSearch.toLowerCase()),
      ),
    [toolOptions, toolSearch],
  );
  const connectedTools = filteredToolOptions.filter((opt) => opt.connected);
  const availableTools = filteredToolOptions.filter((opt) => !opt.connected);
  const showsNoToolRow =
    !toolSearch || "tasks with no tools".includes(toolSearch.toLowerCase());

  const scopeTabCount = scope !== "you" ? 1 : 0;
  const teamTabCount = selectedTeamMembers.length;
  const toolsTabCount = selectedTools.length;
  const activeFilterCount = scopeTabCount + teamTabCount + toolsTabCount;
  const hasActiveFilters = activeFilterCount > 0;

  const labelForTool = (value) =>
    toolOptions.find((opt) => opt.value === value)?.label || value;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-3 w-full flex-wrap">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
          <span className="page-eyebrow">Task Library</span>
          <span
            role="tablist"
            aria-label="Library scope"
            className="ml-1 inline-flex items-center rounded-full border border-border/60 bg-muted/40 p-0.5"
          >
            <button
              type="button"
              role="tab"
              aria-selected={scope === "you"}
              onClick={() => onScopeChange?.("you")}
              className={cn(
                "px-2.5 py-0.5 text-[11px] rounded-full transition-colors",
                scope === "you"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Your Tasks
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === "proven"}
              onClick={() => onScopeChange?.("proven")}
              className={cn(
                "px-2.5 py-0.5 text-[11px] rounded-full transition-colors",
                scope === "proven"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Proven
            </button>
          </span>
          <span className="text-[11px] text-muted-foreground/70 tabular-nums whitespace-nowrap">
            {count} {count === 1 ? "Task" : "Tasks"}
          </span>
        </div>

        <div className="flex items-center gap-3 justify-end">
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-active={hasActiveFilters ? "true" : "false"}
              className="interactive-chip shrink-0 h-8 px-2.5 text-sm focus-visible:outline-none focus-visible:ring-0"
            >
              <Filter className="h-3.5 w-3.5 mr-1.5 opacity-70" />
              <span className="flex items-center gap-1.5">
                Filter
                {hasActiveFilters && (
                  <span className="inline-flex items-center justify-center min-w-[1.125rem] h-4 px-1 rounded-full bg-foreground/10 text-foreground text-[11px] font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </span>
              <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[20rem] p-0 max-h-[32rem] flex flex-col">
            <div className="flex items-center gap-1 p-1 m-2 rounded-md bg-muted/60 shrink-0">
              {FILTER_TABS.map(({ id, label }) => {
                const tabCount =
                  id === "show" ? scopeTabCount : id === "team" ? teamTabCount : toolsTabCount;
                const active = filterTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setFilterTab(id)}
                    className={cn(
                      "flex-1 h-7 rounded-sm text-xs font-medium transition-colors inline-flex items-center justify-center gap-1.5",
                      active
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {label}
                    {tabCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full bg-foreground/10 text-foreground text-[10px] font-medium">
                        {tabCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden pb-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
              {filterTab === "show" && (
                <div className="py-1">
                  {SHOW_OPTIONS.map(({ id, label, Icon }) => (
                    <Row
                      key={id}
                      icon={Icon}
                      label={label}
                      mode="single"
                      active={scope === id}
                      count={counts[id] ?? 0}
                      onClick={() => onScopeChange?.(id)}
                    />
                  ))}
                </div>
              )}

              {filterTab === "team" && (
                <>
                  <div className="px-2 pt-1 pb-2">
                    <Input
                      placeholder="Search people…"
                      value={ownerSearch}
                      onChange={(e) => setOwnerSearch(e.target.value)}
                      className="h-8 text-xs rounded-md"
                    />
                  </div>
                  <div className="py-1">
                    {filteredTeamMembers.map((member) => {
                      const active = member.isCurrentUser
                        ? scope === "you"
                        : selectedTeamMembers.includes(member.id);
                      return (
                        <Row
                          key={member.id}
                          icon={User}
                          label={member.isCurrentUser ? "You" : member.name}
                          mode="multi"
                          active={active}
                          onClick={() => onToggleTeamMember?.(member.id, member.isCurrentUser)}
                        />
                      );
                    })}
                    {filteredTeamMembers.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        No people match "{ownerSearch}"
                      </div>
                    )}
                  </div>
                </>
              )}

              {filterTab === "tools" && (
                <>
                  <div className="px-2 pt-1 pb-2">
                    <Input
                      placeholder="Search tools…"
                      value={toolSearch}
                      onChange={(e) => setToolSearch(e.target.value)}
                      className="h-8 text-xs rounded-md"
                    />
                  </div>
                  {showsNoToolRow && (
                    <div className="py-1">
                      <Row
                        label="Tasks with no tools"
                        count={noToolCount}
                        mode="multi"
                        active={selectedTools.includes(NO_TOOL_VALUE)}
                        onClick={() => onToggleTool?.(NO_TOOL_VALUE)}
                      />
                    </div>
                  )}
                  {connectedTools.length > 0 && (
                    <>
                      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
                        Connected
                      </div>
                      <div className="py-1">
                        {connectedTools.map((opt) => (
                          <Row
                            key={opt.value}
                            label={
                              <span className="flex items-center gap-2">
                                <span
                                  aria-hidden
                                  className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                                />
                                {opt.label}
                              </span>
                            }
                            count={opt.count}
                            mode="multi"
                            active={selectedTools.includes(opt.value)}
                            onClick={() => onToggleTool?.(opt.value)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {availableTools.length > 0 && (
                    <>
                      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
                        Available
                      </div>
                      <div className="py-1">
                        {availableTools.map((opt) => (
                          <Row
                            key={opt.value}
                            label={
                              <span className="flex items-center gap-2">
                                <span
                                  aria-hidden
                                  className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40"
                                />
                                {opt.label}
                              </span>
                            }
                            count={opt.count}
                            mode="multi"
                            active={selectedTools.includes(opt.value)}
                            onClick={() => onToggleTool?.(opt.value)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {filteredToolOptions.length === 0 && toolSearch && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No tools match "{toolSearch}"
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t px-2 py-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  onClearAll?.();
                  setOwnerSearch("");
                  setToolSearch("");
                }}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="h-7 px-3 text-xs font-medium rounded-md bg-foreground text-background hover:bg-foreground/90"
              >
                Done
              </button>
            </div>
          </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-2 mt-3">
        <span className="text-xs text-muted-foreground">Active filters:</span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
          {scopeChipLabel || "You"}
          <button
            type="button"
            onClick={onResetScope}
            className="hover:bg-accent-foreground/10 rounded-full p-0.5 transition-colors"
            aria-label={`Exit ${scopeChipLabel || "You"}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
        {selectedTools.map((tool) => {
          const label = tool === NO_TOOL_VALUE ? "No tools" : labelForTool(tool);
          return (
            <span
              key={tool}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full"
            >
              <Tag className="w-3 h-3" />
              <span>{label}</span>
              <button
                type="button"
                onClick={() => onToggleTool?.(tool)}
                className="hover:bg-accent-foreground/10 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${label}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        {onSaveView && (
          <button
            type="button"
            onClick={onSaveView}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-1"
          >
            + Save as view
          </button>
        )}
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground underline transition-colors ml-auto"
        >
          Clear all
        </button>
      </div>
    </div>
  );
};

export default TasksFilterBar;
